// src/pages/funnels/FunnelDetail.jsx
// /funnels/:funnelId — Steps | Stats | Leads | Settings.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { authFetch } from '../../lib/authFetch';
import {
  Card, CardContent, Button, Input, Select, Modal, ModalFooter, Badge,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui';
import { formatNumber, copyToClipboard, cn } from '../../lib/utils';
import {
  ArrowLeft, ArrowUp, ArrowDown, Plus, Trash2, Copy, ExternalLink,
  Edit, Loader2, Globe, Check, Rocket, CircleOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FunnelStats from './FunnelStats';
import LeadsTable from './LeadsTable';

const PAGES_DOMAIN = 'pages.revhapi.com';

const STEP_TYPES = [
  { value: 'optin', label: 'Opt-in' },
  { value: 'thankyou', label: 'Thank You' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'sales', label: 'Sales / Offer' },
  { value: 'custom', label: 'Custom' },
];

function stepTypeLabel(type) {
  return STEP_TYPES.find(t => t.value === type)?.label || type;
}

export default function FunnelDetail() {
  const { funnelId } = useParams();
  const navigate = useNavigate();
  const { user, profile, isPro, isAdmin } = useAuth();

  const [funnel, setFunnel] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Add-step modal
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [newStepType, setNewStepType] = useState('custom');
  const [addingStep, setAddingStep] = useState(false);

  // Settings form
  const [settingsForm, setSettingsForm] = useState(null);
  const [domains, setDomains] = useState([]);
  const [seriesCampaigns, setSeriesCampaigns] = useState([]);

  const canUseCustomDomains = isPro || isAdmin || profile?.role === 'superadmin';

  const loadFunnel = useCallback(async () => {
    try {
      const res = await authFetch(`/api/funnels?resource=funnels&funnelId=${funnelId}`);
      const data = await res.json();
      if (!data.success) {
        toast.error(data.error || 'Funnel not found');
        navigate('/funnels');
        return;
      }
      setFunnel(data.funnel);
      setSteps(data.steps || []);
      setSettingsForm({
        name: data.funnel.name,
        custom_domain_id: data.funnel.custom_domain_id || '',
        custom_slug: data.funnel.custom_slug || '',
        enroll_campaign_id: data.funnel.settings?.enroll_campaign_id || '',
        webhook_url: data.funnel.settings?.webhook_url || '',
        contact_tags: (data.funnel.settings?.contact_tags || []).join(', '),
      });
    } catch (e) {
      console.error('Funnel load error:', e);
      toast.error('Failed to load funnel');
    } finally {
      setLoading(false);
    }
  }, [funnelId, navigate]);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);

  // Verified custom domains + email series for the Settings tab (owner-scoped
  // reads via the RLS-protected browser client, same pattern as LandingPages).
  useEffect(() => {
    if (!user) return;
    (async () => {
      if (canUseCustomDomains) {
        const { data } = await supabase
          .from('custom_domains').select('id, domain')
          .eq('user_id', user.id).eq('status', 'verified');
        setDomains(data || []);
      }
      const { data: campaigns } = await supabase
        .from('email_campaigns').select('id, name, status')
        .eq('user_id', user.id).eq('type', 'series')
        .order('created_at', { ascending: false });
      setSeriesCampaigns(campaigns || []);
    })();
  }, [user, canUseCustomDomains]);

  const publicUrl = funnel
    ? funnel.custom_domain_id && funnel.custom_domains?.domain
      ? `https://${funnel.custom_domains.domain}/${funnel.custom_slug || ''}`
      : `https://${PAGES_DOMAIN}/${funnel.slug}`
    : '';

  const stepUrl = (step) =>
    step.step_slug ? `${publicUrl}/${step.step_slug}` : publicUrl;

  const postFunnels = async (resource, payload) => {
    const res = await authFetch(`/api/funnels?resource=${resource}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  };

  // ── Publish / unpublish ────────────────────────────────────────────────────
  const handlePublishToggle = async () => {
    setPublishing(true);
    try {
      const action = funnel.status === 'published' ? 'unpublish' : 'publish';
      const data = await postFunnels('funnels', { action, funnelId: funnel.id });
      if (!data.success) return toast.error(data.error || 'Failed');
      setFunnel(f => ({ ...f, ...data.funnel, custom_domains: f.custom_domains }));
      toast.success(action === 'publish' ? 'Funnel is live! 🚀' : 'Funnel unpublished');
    } finally {
      setPublishing(false);
    }
  };

  // ── Steps ──────────────────────────────────────────────────────────────────
  const handleMoveStep = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const reordered = [...steps];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setSteps(reordered); // optimistic
    const data = await postFunnels('steps', {
      action: 'reorder',
      funnelId: funnel.id,
      orderedStepIds: reordered.map(s => s.id),
    });
    if (!data.success) {
      toast.error(data.error || 'Failed to reorder');
    }
    loadFunnel(); // root slug may have swapped — refresh from server
  };

  const handleAddStep = async () => {
    setAddingStep(true);
    try {
      const data = await postFunnels('steps', {
        action: 'create',
        funnelId: funnel.id,
        name: newStepName.trim() || 'New Step',
        step_type: newStepType,
      });
      if (!data.success) return toast.error(data.error || 'Failed to add step');
      setShowAddStep(false);
      setNewStepName('');
      toast.success('Step added');
      loadFunnel();
    } finally {
      setAddingStep(false);
    }
  };

  const handleDuplicateStep = async (step) => {
    const data = await postFunnels('steps', { action: 'duplicate', stepId: step.id });
    if (!data.success) return toast.error(data.error || 'Failed to duplicate');
    toast.success('Step duplicated');
    loadFunnel();
  };

  const handleDeleteStep = async (step) => {
    if (!window.confirm(`Delete step "${step.name}"?`)) return;
    const data = await postFunnels('steps', { action: 'delete', stepId: step.id });
    if (!data.success) return toast.error(data.error || 'Failed to delete');
    toast.success('Step deleted');
    loadFunnel();
  };

  // ── Settings ───────────────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        action: 'update',
        funnelId: funnel.id,
        name: settingsForm.name,
        settings: {
          enroll_campaign_id: settingsForm.enroll_campaign_id || undefined,
          webhook_url: settingsForm.webhook_url.trim() || undefined,
          contact_tags: settingsForm.contact_tags
            .split(',').map(t => t.trim()).filter(Boolean),
        },
      };
      if (canUseCustomDomains) {
        payload.custom_domain_id = settingsForm.custom_domain_id || null;
        if (settingsForm.custom_domain_id) payload.custom_slug = settingsForm.custom_slug;
      }
      const data = await postFunnels('funnels', payload);
      if (!data.success) return toast.error(data.error || 'Failed to save');
      toast.success('Settings saved');
      loadFunnel();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFunnel = async () => {
    if (!window.confirm(`Delete "${funnel.name}"? Its pages, leads and stats will be permanently removed.`)) return;
    const data = await postFunnels('funnels', { action: 'delete', funnelId: funnel.id });
    if (!data.success) return toast.error(data.error || 'Failed to delete');
    toast.success('Deleted');
    navigate(funnel.kind === 'page' ? '/landing-pages' : '/funnels');
  };

  const handleCopyUrl = async () => {
    await copyToClipboard(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading || !funnel || !settingsForm) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Funnels and standalone landing pages share this screen; a page is a
  // one-step funnel, so the step-management affordances are hidden for it and
  // "back" returns to whichever list the user came from.
  const isPage = funnel.kind === 'page';
  const backHref = isPage ? '/landing-pages' : '/funnels';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Link to={backHref} className="p-2 hover:bg-muted rounded-lg mt-0.5">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold truncate">{funnel.name}</h1>
              <Badge
                className={cn(
                  'text-[10px] px-1.5 py-0.5',
                  funnel.status === 'published' ? 'bg-green-500' : 'bg-muted-foreground/60'
                )}
              >
                {funnel.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
              <Globe className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{publicUrl.replace('https://', '')}</span>
              <button onClick={handleCopyUrl} className="p-1 hover:bg-muted rounded" title="Copy URL">
                {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
              </button>
              {funnel.status === 'published' && (
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                  className="p-1 hover:bg-muted rounded" title="Open">
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>
        <Button onClick={handlePublishToggle} disabled={publishing}
          variant={funnel.status === 'published' ? 'outline' : 'default'}>
          {publishing
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : funnel.status === 'published'
              ? <CircleOff className="h-4 w-4 mr-2" />
              : <Rocket className="h-4 w-4 mr-2" />}
          {funnel.status === 'published' ? 'Unpublish' : 'Publish'}
        </Button>
      </div>

      <Tabs defaultValue="steps">
        <TabsList>
          <TabsTrigger value="steps">{isPage ? 'Page' : 'Steps'}</TabsTrigger>
          <TabsTrigger value="stats">Stats</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* ── STEPS ── */}
        <TabsContent value="steps">
          <div className="space-y-3 mt-4">
            {steps.map((step, index) => (
              <Card key={step.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Ordering and numbering are meaningless for a one-page
                        landing page — there is nothing to reorder against. */}
                    {!isPage && (
                      <>
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => handleMoveStep(index, -1)} disabled={index === 0}
                            className="p-0.5 rounded hover:bg-muted disabled:opacity-30 text-muted-foreground">
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleMoveStep(index, 1)} disabled={index === steps.length - 1}
                            className="p-0.5 rounded hover:bg-muted disabled:opacity-30 text-muted-foreground">
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                      </>
                    )}
                    <div className="flex-1 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{step.name}</span>
                        <Badge className="bg-muted text-muted-foreground text-[10px] px-1.5 py-0.5">
                          {stepTypeLabel(step.step_type)}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {stepUrl(step).replace('https://', '')}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatNumber(step.total_views || 0)} views · {formatNumber(step.total_optins || 0)} opt-ins
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" onClick={() => navigate(`/funnels/${funnel.id}/edit/${step.id}`)}>
                        <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
                      </Button>
                      {/* Duplicating or deleting the only page would leave the
                          landing page with zero or two pages — neither is a
                          valid single page. */}
                      {!isPage && (
                        <>
                          <button onClick={() => handleDuplicateStep(step)}
                            className="p-2 hover:bg-muted rounded-lg text-muted-foreground" title="Duplicate step">
                            <Copy className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteStep(step)}
                            className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-destructive"
                            title="Delete step">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {!isPage && (
              <Button variant="outline" onClick={() => setShowAddStep(true)}>
                <Plus className="h-4 w-4 mr-2" /> Add Step
              </Button>
            )}
          </div>
        </TabsContent>

        {/* ── STATS ── */}
        <TabsContent value="stats">
          <div className="mt-4">
            <FunnelStats funnelId={funnel.id} />
          </div>
        </TabsContent>

        {/* ── LEADS ── */}
        <TabsContent value="leads">
          <div className="mt-4">
            <LeadsTable funnelId={funnel.id} funnelName={funnel.name} />
          </div>
        </TabsContent>

        {/* ── SETTINGS ── */}
        <TabsContent value="settings">
          <div className="mt-4 max-w-2xl space-y-5">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Funnel name</label>
                  <Input value={settingsForm.name}
                    onChange={e => setSettingsForm(f => ({ ...f, name: e.target.value }))} />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Domain</label>
                  {canUseCustomDomains ? (
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Select value={settingsForm.custom_domain_id}
                        onChange={e => setSettingsForm(f => ({ ...f, custom_domain_id: e.target.value }))}>
                        <option value="">{PAGES_DOMAIN} (default)</option>
                        {domains.map(d => <option key={d.id} value={d.id}>{d.domain}</option>)}
                      </Select>
                      {settingsForm.custom_domain_id && (
                        <Input value={settingsForm.custom_slug} placeholder="custom-slug"
                          onChange={e => setSettingsForm(f => ({ ...f, custom_slug: e.target.value }))} />
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Served on {PAGES_DOMAIN}/{funnel.slug}.{' '}
                      <Link to="/subscribe" className="text-primary hover:underline">Upgrade to Pro</Link>{' '}
                      to use your own domain.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="font-semibold text-sm">When someone opts in</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Every lead is saved to this funnel and to your Email Marketing contacts.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Enroll in email series</label>
                  <Select value={settingsForm.enroll_campaign_id}
                    onChange={e => setSettingsForm(f => ({ ...f, enroll_campaign_id: e.target.value }))}>
                    <option value="">Don't enroll automatically</option>
                    {seriesCampaigns.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.status !== 'sending' && c.status !== 'scheduled' ? ` (${c.status})` : ''}
                      </option>
                    ))}
                  </Select>
                  {seriesCampaigns.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      No email series yet — create one in Email Marketing to send an automatic welcome sequence.
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Contact tags</label>
                  <Input value={settingsForm.contact_tags} placeholder="e.g. webinar, vip (comma-separated)"
                    onChange={e => setSettingsForm(f => ({ ...f, contact_tags: e.target.value }))} />
                  <p className="text-xs text-muted-foreground mt-1">
                    Added to each contact — a funnel:{funnel.slug} tag is always included.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Webhook URL</label>
                  <Input value={settingsForm.webhook_url} placeholder="https://hooks.zapier.com/..."
                    onChange={e => setSettingsForm(f => ({ ...f, webhook_url: e.target.value }))} />
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll POST each new lead as JSON — works with Zapier, Make, GoHighLevel, ActiveCampaign, etc.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={handleDeleteFunnel}
                className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4 mr-2" /> Delete Funnel
              </Button>
              <Button onClick={handleSaveSettings} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Settings
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add step modal */}
      <Modal isOpen={showAddStep} onClose={() => setShowAddStep(false)} title="Add Step">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Step name</label>
            <Input value={newStepName} onChange={e => setNewStepName(e.target.value)}
              placeholder="e.g. Bridge Page" autoFocus />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Type</label>
            <Select value={newStepType} onChange={e => setNewStepType(e.target.value)}>
              {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowAddStep(false)}>Cancel</Button>
          <Button onClick={handleAddStep} disabled={addingStep}>
            {addingStep && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Step
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
